import { useMutation, useQuery } from "@apollo/client"

import useInviteSalonUserModal from "./InviteSalonUserModal/InviteSalonUserModal"
import MobileSalonUsersTableView from "./SalonUsersTableView/MobileSalonUsersTableView"
import SalonUsersTableView from "./SalonUsersTableView/SalonUsersTableView"
import {
	inviteSalonUserButtonVisible,
	makeSelfSalonOwnerButtonVisible,
} from "./SalonUsersTableView/SalonUserstableView.logic"
import useUpdateSalonUserRoleModal from "./UpdateSalonUserRoleModal/UpdateSalonUserRoleModal"
import styles from "./UsersSetting.module.scss"

import { useMe } from "@/lib/graphql/me"
import { useUserPermission } from "@/main/components/common_features/salon/userPermission"
import { getFragmentData, graphql } from "@/main/gql/__generated__"
import {
	DeleteSalonUserMutation,
	DeleteSalonUserMutationVariables,
	InviteSalonUserInput,
	InviteSalonUserMutation,
	InviteSalonUserMutationVariables,
	MakeSelfSalonOwnerMutation,
	MakeSelfSalonOwnerMutationVariables,
	SalonUserAllFieldFragment,
	SalonUserAllFieldFragmentDoc,
	SwitchSalonOwnerMutation,
	SwitchSalonOwnerMutationVariables,
	UpdateSalonUserRoleMutation,
	UpdateSalonUserRoleMutationVariables,
} from "@/main/gql/__generated__/graphql"
import { useMediaQueryContext } from "@/provider/MediaQueryProvider"

import ContentHeader from "../common/ContentHeader/ContentHeader"
import EmitModalButton from "../common/EmitModalButton/EmitModalButton"

const GetSalonUsers = graphql(/* GraphQL */ `
	query GetSalonUsers {
		salonUsers {
			...salonUserAllField
		}
	}
`)

const UpdateSalonUserRole = graphql(/* GraphQL */ `
	mutation UpdateSalonUserRole($input: UpdateSalonUserRoleInput!) {
		updateSalonUserRole(input: $input) {
			salonUser {
				...salonUserAllField
			}
		}
	}
`)

const SwitchSalonOwner = graphql(/* GraphQL */ `
	mutation SwitchSalonOwner($input: SwitchSalonOwnerInput!) {
		switchSalonOwner(input: $input) {
			ok
		}
	}
`)

const DeleteSalonUser = graphql(/* GraphQL */ `
	mutation DeleteSalonUser($input: DeleteSalonUserInput!) {
		deleteSalonUser(input: $input) {
			ok
		}
	}
`)

const InviteSalonUser = graphql(/* GraphQL */ `
	mutation InviteSalonUser($input: InviteSalonUserInput!) {
		inviteSalonUser(input: $input) {
			ok
		}
	}
`)

// input は salon_id / user_id を context から取得するため実質不要だが、schema 上 input は必須なので空オブジェクトを渡す
const MakeSelfSalonOwner = graphql(/* GraphQL */ `
	mutation MakeSelfSalonOwner {
		makeSelfSalonOwner(input: {}) {
			ok
		}
	}
`)

const UsersSetting = () => {
	const { isMobile } = useMediaQueryContext()
	// queries
	const { userPermission, refetch: refetchUserPermission } = useUserPermission()

	const { data, refetch } = useQuery(GetSalonUsers, {
		fetchPolicy: "cache-first",
	})
	const salonUsers =
		getFragmentData(SalonUserAllFieldFragmentDoc, data?.salonUsers) ?? []

	const { salonUser: mySalonUser } = useMe()

	// mutations
	const [inviteSalonUser] = useMutation<
		InviteSalonUserMutation,
		InviteSalonUserMutationVariables
	>(InviteSalonUser)
	const [updateSalonUserRole] = useMutation<
		UpdateSalonUserRoleMutation,
		UpdateSalonUserRoleMutationVariables
	>(UpdateSalonUserRole)
	const [switchSalonOwner] = useMutation<
		SwitchSalonOwnerMutation,
		SwitchSalonOwnerMutationVariables
	>(SwitchSalonOwner)
	const [deleteSalonUser] = useMutation<
		DeleteSalonUserMutation,
		DeleteSalonUserMutationVariables
	>(DeleteSalonUser)
	const [makeSelfSalonOwner] = useMutation<
		MakeSelfSalonOwnerMutation,
		MakeSelfSalonOwnerMutationVariables
	>(MakeSelfSalonOwner)

	// FIXME: やっぱりconfirmないやつ欲しい
	// modals
	const {
		render: renderInviteSalonUserModal,
		openModal: openInviteSalonUserModal,
	} = useInviteSalonUserModal({
		inviteSalonUser: async (input: InviteSalonUserInput) => {
			return await inviteSalonUser({
				variables: {
					input,
				},
			}).then(() => {
				refetch() // refetch because new salon user will be added
			}) // cast a returning value to void
		},
	})
	const {
		render: renderUpdateSalonUserRoleModal,
		openModal: openUpdateSalonUserRoleModal,
	} = useUpdateSalonUserRoleModal({
		updateSalonUserRole: async (input) => {
			return await updateSalonUserRole({
				variables: {
					input,
				},
			}).then(() => undefined) // cast a returning value to void
		},
	})

	// onClick functions
	const onClickSwitchOwnerButton = (userId: string) => {
		if (!window.confirm("オーナーを変更しますか？")) {
			return
		}
		switchSalonOwner({
			variables: {
				input: {
					userId,
				},
			},
		}).then(() => {
			refetch()
			refetchUserPermission() // 自分のpermissionが変わるためrefetch
			alert("オーナーを変更しました。")
		})
	}

	// souhl_admin 限定。自分をこの店舗の owner にする (その後 switchSalonOwner で本来の owner に引き継ぐ運用)
	const onClickMakeSelfSalonOwnerButton = () => {
		if (!window.confirm("自分をこの店舗の管理者(オーナー)にしますか？")) {
			return
		}
		makeSelfSalonOwner().then(() => {
			refetch()
			refetchUserPermission() // 自分のpermissionが変わるためrefetch
			alert("自分をこの店舗の管理者(オーナー)にしました。")
		})
	}

	const onClickReinviteSalonUserButton = (
		salonUser: SalonUserAllFieldFragment
	) => {
		if (!window.confirm("招待メールを再送しますか？")) {
			return
		}
		inviteSalonUser({
			variables: {
				input: {
					email: salonUser.email,
					role: salonUser.role,
				},
			},
		}).then(() => {
			alert(
				"招待メールを再送しました。\n※届くまでに数十秒かかる場合があります。"
			)
		})
	}

	const onClickDeleteSalonUserButton = (userId: string) => {
		if (!window.confirm("ユーザーを削除しますか？")) {
			return
		}
		deleteSalonUser({
			variables: {
				input: {
					userId,
				},
			},
		}).then(() => {
			refetch() // refetch because the user will be deleted
			alert("ユーザーを削除しました。")
		})
	}

	return (
		<div className={styles.users_setting}>
			{renderInviteSalonUserModal()}
			{renderUpdateSalonUserRoleModal()}
			<ContentHeader
				title="ユーザー"
				description="店舗に所属するユーザー一覧です。"
				emittableModalButton={() => (
					<span className={styles.header_buttons}>
						<EmitModalButton
							text={"オーナーを自分にする"}
							onClick={onClickMakeSelfSalonOwnerButton}
							hide={!makeSelfSalonOwnerButtonVisible(userPermission)}
							variant="blue"
						/>
						<EmitModalButton
							text={"招待"}
							onClick={openInviteSalonUserModal}
							hide={!inviteSalonUserButtonVisible(userPermission)}
						/>
					</span>
				)}
			/>
			{mySalonUser && userPermission && !isMobile && (
				<SalonUsersTableView
					mySalonUser={mySalonUser}
					salonUsers={salonUsers}
					userPermission={userPermission}
					openUpdateSalonUserRoleModal={openUpdateSalonUserRoleModal}
					onClickReinviteSalonUserButton={onClickReinviteSalonUserButton}
					onClickSwitchOwnerButton={onClickSwitchOwnerButton}
					onClickDeleteSalonUserButton={onClickDeleteSalonUserButton}
				/>
			)}
			{mySalonUser && userPermission && isMobile && (
				<MobileSalonUsersTableView
					mySalonUser={mySalonUser}
					salonUsers={salonUsers}
					userPermission={userPermission}
					openUpdateSalonUserRoleModal={openUpdateSalonUserRoleModal}
					onClickReinviteSalonUserButton={onClickReinviteSalonUserButton}
					onClickSwitchOwnerButton={onClickSwitchOwnerButton}
					onClickDeleteSalonUserButton={onClickDeleteSalonUserButton}
				/>
			)}
		</div>
	)
}

export default UsersSetting
